import { InputText } from 'component-library/index';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { memo } from 'react';
import { useField } from '../../../providers/ModernAcquisitionProvider/useField';
import { TErrorType } from '../../../providers/ModernAcquisitionProvider/types';
import SlideDown from '../../SlideDown/SlideDown';
import { TReactParseable } from '../../../utils/general-types';
import SessionStorage from '../../../utils/SessionStorage';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import useBriteVerify, { IPhoneResponse } from '../../../hooks/useBriteVerify';
import { generateFieldErrorMessage } from '../../../utils/error-message';

export interface IPhoneNumberFieldWithProviderProps {
    fieldKey: 'phoneNumber'
    name: 'Phone Number';
    label?: string;
    id?: string;
    helperText?: string;
    disabled?: boolean;
    onBlurCallback?: () => void;
    onFocusCallback?: () => void;
    disclaimerMessage?: TReactParseable;
    disclaimerClassName?: string;
}
type TValidationResult = false | IPhoneResponse | "skipped";
type TLastPhone = {
    value: string;
    result: boolean;
    validationResult?: TValidationResult
}

const MemoizedInputText = memo(InputText);

export const PhoneNumberFieldWithProvider = memo(function PhoneNumberFieldWithProvider(props: IPhoneNumberFieldWithProviderProps & React.HTMLAttributes<HTMLInputElement>, ...rest) {

    const { field, updateField } = useField(props.fieldKey);
    const { phoneValidationApi } = useBriteVerify();
    const [isFocused, setIsFocused] = useState(false);
    const [lastPhone, setLastPhone] = useState<TLastPhone>({ value: '', result: false });
    const [fieldErrorMessage, setFieldErrorMessage] = useState<string>(generateFieldErrorMessage('Phone Number', 'missingValue'));
    const [plist] = useState<Array<String>>(PathUtils.getMetaPropertyValue('cq:plist')?.split(',').map(phone => atob(phone)) ?? []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const formatted = event.target.value.replaceAll(/[^0-9]/g, '');
        updateField({ ...field, value: formatted });
    }

    const handleBlur = async (): Promise<void> => {
        const trimmedValue = field.value?.trim() ?? '';
        const isValid = trimmedValue.length < 1 ? false : await validatePhone(trimmedValue);
        setIsFocused(false);
        if (isValid) {
            updateField({ ...field, state: 'success', errorType: 'None' })
        } else {
            const error: TErrorType = trimmedValue.length === 10 ? 'thirdParty' : 'requiredField';
            updateField({ ...field, state: 'error', errorType: error })
        }
        props.onBlurCallback && props.onBlurCallback();
    }

    const handleFocus = (): void => {
        setIsFocused(true);
        if (field.state === 'success') {
            updateField({ ...field, state: 'neutral' })
        }
        props.onFocusCallback && props.onFocusCallback();
    }

    const formatPhoneNumber = (phone: string | undefined): string => {
        if (typeof phone !== 'string') {
            return '';
        }
        if (phone.length > 3 && phone.length <= 6) {
            return `${phone.slice(0, 3)}-${phone.slice(3)}`
        } else if (phone.length > 6) {
            return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`
        }
        return phone;
    };

    const validatePhone = async (value: string): Promise<boolean> => {
        if (value.length < 10 || plist?.includes(value)) {
            setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
            return false;
        }
        const isAuthor = ['4502', '3000'].some((entry) => window.location.port.includes(entry));
        if (
            (!SessionStorage.contains('adobeMode') && !isAuthor) ||
            (SessionStorage.contains('adobeMode') && SessionStorage.contains('usePhoneApi'))
        ) {
            if (value === lastPhone?.value) {
                if (lastPhone.validationResult) {
                    SessionStorage.set('bvrslt', btoa(JSON.stringify(lastPhone.validationResult)));
                    setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
                } else {
                    setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'thirdParty', 'Number'));
                }
                setLastPhone({ value: value, result: lastPhone.result, validationResult: lastPhone.validationResult });
                return lastPhone.result;
            }
            const validationResult: TValidationResult = await phoneValidationApi(value);
            if (validationResult) {
                SessionStorage.set('bvrslt', btoa(JSON.stringify(validationResult)));
                setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'missingValue'));
            } else {
                setFieldErrorMessage(generateFieldErrorMessage('Phone Number', 'thirdParty', 'Number'));
            }

            setLastPhone({ value: value, result: !!validationResult, validationResult: validationResult });
            return !!validationResult;
        }
        setLastPhone({ value: value, result: true });
        return true;
    };

    return (
        <div>
            <MemoizedInputText
                required
                name={props.name}
                label={props.label || props.name}
                helperText={props.helperText}
                id={props.id}
                value={formatPhoneNumber(field.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                type='tel'
                inputMode='numeric'
                errorMessage={fieldErrorMessage}
                state={field.state}
                maxLength={12}
                disabled={props.disabled}
                {...rest}
            />
            {props.disclaimerMessage &&
                <SlideDown
                    open={isFocused}
                    className={props.disclaimerClassName}
                    tabIndex={-1}
                >
                    {props.disclaimerMessage}
                </SlideDown>
            }
        </div>
    )

});

export default PhoneNumberFieldWithProvider;